/**
 * <cart-drawer> — the mini-cart.
 *
 * Owns every cart mutation on the storefront. Add, quantity change and remove all go
 * through the AJAX Cart API and then re-render this section via the Section Rendering
 * API, so the drawer's Liquid stays the single source of truth for its markup and no
 * cart interaction ever causes a page load.
 *
 * Opening is driven by two things:
 *   - a click on any `[data-cart-toggle]` (the header bag)
 *   - the `cart:added` event, dispatched by product forms after a successful add
 *
 * Accessibility: the panel is a modal dialog with a focus trap, Escape closes, focus
 * returns to whatever opened it, and background scrolling is locked while open.
 */

// Wrapped so nothing here reaches global scope: these are classic scripts, and a
// top-level name shared with another file makes the whole file throw before it can
// register its element. Also what makes a duplicate <script> tag for this file safe.
(() => {
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.cart-drawer__panel');
      this.errorElement = this.querySelector('[data-cart-error]');

      this.onKeydown = this.onKeydown.bind(this);
      this.open = this.open.bind(this);
      this.close = this.close.bind(this);
      this.onCartAdded = this.onCartAdded.bind(this);

      this.bindStaticListeners();
      this.bindPanelListeners();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('cart:added', this.onCartAdded);
      this.toggles?.forEach((toggle) => toggle.removeEventListener('click', this.onToggleClick));
    }

    /** Listeners on nodes outside the drawer, bound once for the element's lifetime. */
    bindStaticListeners() {
      this.onToggleClick = (event) => {
        event.preventDefault();
        this.open(event.currentTarget);
      };

      this.toggles = document.querySelectorAll('[data-cart-toggle]');
      this.toggles.forEach((toggle) => toggle.addEventListener('click', this.onToggleClick));

      document.addEventListener('cart:added', this.onCartAdded);
    }

    /**
     * Listeners on nodes inside the panel. Re-bound after every re-render, because the
     * Section Rendering API replaces the panel's markup wholesale.
     */
    bindPanelListeners() {
      // All of them: the header X and the empty state's "continue shopping" both close.
      this.querySelectorAll('[data-cart-close]').forEach((button) => {
        button.addEventListener('click', this.close);
      });
      this.querySelector('[data-cart-scrim]')?.addEventListener('click', this.close);

      this.querySelectorAll('[data-quantity-change]').forEach((button) => {
        button.addEventListener('click', () => {
          const stepper = button.closest('.quantity-stepper');
          const input = stepper.querySelector('[data-quantity-input]');
          const next = Number(input.value) + Number(button.dataset.quantityChange);
          this.changeLine(stepper.dataset.lineKey, Math.max(next, 0));
        });
      });

      this.querySelectorAll('[data-quantity-input]').forEach((input) => {
        input.addEventListener('change', () => {
          const stepper = input.closest('.quantity-stepper');
          this.changeLine(stepper.dataset.lineKey, Math.max(Number(input.value), 0));
        });
      });

      this.querySelectorAll('[data-cart-remove]').forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          const key = link.closest('[data-line-key]').dataset.lineKey;
          this.changeLine(key, 0);
        });
      });
    }

    /* ---- Open / close ---- */

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(opener = null) {
      if (opener) this.opener = opener;

      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-open'));

      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeydown);

      this.querySelector('[data-cart-close]')?.focus();
    }

    close() {
      if (!this.isOpen) return;

      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeydown);

      const hide = () => {
        this.hidden = true;
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        hide();
      } else {
        this.panel.addEventListener('transitionend', hide, { once: true });
      }

      this.opener?.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab' || !this.isOpen) return;

      const focusable = [...this.panel.querySelectorAll(FOCUSABLE)].filter(
        (element) => element.offsetParent !== null
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onCartAdded() {
      this.refresh().then(() => this.open());
    }

    /* ---- Cart mutations ---- */

    async changeLine(key, quantity) {
      this.setBusy(true);

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity }),
        });

        if (!response.ok) throw new Error(`cart/change.js responded ${response.status}`);

        await this.refresh();
      } catch (error) {
        console.error(error);
        this.showError();
      } finally {
        this.setBusy(false);
      }
    }

    /**
     * Re-renders the drawer from the server and swaps in the new panel. Using the Section
     * Rendering API rather than patching the DOM by hand keeps totals, discounts and item
     * counts consistent with whatever Liquid decides.
     */
    async refresh() {
      const sectionId = this.dataset.sectionId;
      const response = await fetch(
        `${window.Shopify.routes.root}?sections=${encodeURIComponent(sectionId)}`
      );

      if (!response.ok) throw new Error(`Section render responded ${response.status}`);

      const sections = await response.json();
      const markup = sections[sectionId];

      // The markup is this same section re-rendered by our own storefront — first-party,
      // same-origin, Liquid-escaped output, not user input — so assigning it is safe.
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const fresh = parsed.querySelector('cart-drawer');
      if (!fresh) return;

      const wasOpen = this.isOpen;

      this.innerHTML = fresh.innerHTML;
      this.className = fresh.className;
      if (wasOpen) this.classList.add('is-open');

      this.panel = this.querySelector('.cart-drawer__panel');
      this.errorElement = this.querySelector('[data-cart-error]');
      this.bindPanelListeners();

      this.updateCartCount();
    }

    /** Keeps the header bag badge in step with the drawer. */
    updateCartCount() {
      const count = this.querySelectorAll('.cart-drawer__item').length
        ? this.querySelector('.cart-drawer__count')?.textContent.trim()
        : null;

      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        const quantities = [...this.querySelectorAll('[data-quantity-input]')].map((input) =>
          Number(input.value)
        );
        badge.textContent = quantities.reduce((total, quantity) => total + quantity, 0);
      });

      return count;
    }

    setBusy(isBusy) {
      this.classList.toggle('is-busy', isBusy);
      this.panel?.setAttribute('aria-busy', String(isBusy));
    }

    showError() {
      if (!this.errorElement) return;
      this.errorElement.hidden = false;
    }
  }

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', CartDrawer);
  }
})();
